import { useState, useEffect } from 'react'
import { useGameStore } from '@store/gameStore'
import { GameLayout } from '@components/GameLayout'
import './DailyScreen.css'

// 7-day reward calendar
const DAILY_REWARDS = [
    { day: 1, name: '100 Gold', emoji: '💰', type: 'gold', amount: 100 },
    { day: 2, name: '50 Gems', emoji: '💎', type: 'gems', amount: 50 },
    { day: 3, name: '200 Gold', emoji: '💰', type: 'gold', amount: 200 },
    { day: 4, name: '1× Lootbox', emoji: '🎁', type: 'lootbox', amount: 1 },
    { day: 5, name: '100 Gems', emoji: '💎', type: 'gems', amount: 100 },
    { day: 6, name: '500 Gold', emoji: '💰', type: 'gold', amount: 500 },
    { day: 7, name: 'MEGA BELOHNUNG', emoji: '🏆', type: 'mega', amount: 1 },
]

export function DailyScreen() {
    const [claimedToday, setClaimedToday] = useState(false)
    const [showReward, setShowReward] = useState(false)
    const [currentReward, setCurrentReward] = useState<typeof DAILY_REWARDS[0] | null>(null)
    const [streak, setStreak] = useState(1)
    const [timeUntilReset, setTimeUntilReset] = useState('')

    const addGems = useGameStore(state => state.addGems)
    const addGold = useGameStore(state => state.addGold)
    const addPower = useGameStore(state => state.addPower)
    const setScreen = useGameStore(state => state.setScreen)
    const dailyStreak = useGameStore(state => state.dailyStreak)
    const lastDailyClaimGameDay = useGameStore(state => state.lastDailyClaimGameDay)
    const totalGameMinutes = useGameStore(state => state.totalGameMinutes)
    const claimDailyReward = useGameStore(state => state.claimDailyReward)

    // New selectors for improved logic
    const isVIP = useGameStore(state => state.isVIP)
    const lastDailyClaimType = useGameStore(state => state.lastDailyClaimType)
    const upgradeDailyClaimToDoubled = useGameStore(state => state.upgradeDailyClaimToDoubled)
    const setShopTab = useGameStore(state => state.setShopTab)
    const addFreeLootboxSpins = useGameStore(state => state.addFreeLootboxSpins)
    const tutorialPhase = useGameStore(state => state.tutorialPhase)
    const setTutorialPhase = useGameStore(state => state.setTutorialPhase)
    const setTutorialSubStep = useGameStore(state => state.setTutorialSubStep)

    // Initialize streak from store
    useEffect(() => {
        setStreak(dailyStreak || 1)

        // Check if already claimed today in game time
        const currentDay = Math.floor(totalGameMinutes / (24 * 60))
        if (lastDailyClaimGameDay === currentDay) {
            setClaimedToday(true)
        } else {
            setClaimedToday(false)
        }
    }, [dailyStreak, lastDailyClaimGameDay, totalGameMinutes])

    // Countdown timer for next GAME day (every 24 real mins)
    useEffect(() => {
        const updateTimer = () => {
            const ticksPerDay = 24 * 60
            const currentDayTicks = totalGameMinutes % ticksPerDay
            const remainingTicks = ticksPerDay - currentDayTicks

            const gameHours = Math.floor(remainingTicks / 60)
            const gameMinutes = remainingTicks % 60

            setTimeUntilReset(
                `${gameHours.toString().padStart(2, '0')}:${gameMinutes.toString().padStart(2, '0')} Std`
            )
        }

        updateTimer()
    }, [totalGameMinutes])

    // Claim reward
    const handleClaim = () => {
        // Check if we can claim anything
        const alreadyClaimedFree = claimedToday
        const alreadyClaimedDoubled = lastDailyClaimType === 'doubled'

        if (alreadyClaimedDoubled) return // Fully claimed
        if (alreadyClaimedFree && !isVIP) return // Claimed free, not VIP -> done

        // Determine what we are claiming
        const todayReward = DAILY_REWARDS[(streak - 1) % 7]
        setCurrentReward(todayReward)

        // If we already claimed free and are now VIP upgrading
        if (alreadyClaimedFree && isVIP) {
            // Give the reward AGAIN (doubling it essentially)
            applyReward(todayReward)
            upgradeDailyClaimToDoubled()
            setShowReward(true)
            return
        }

        // Normal claim (first time today)
        applyReward(todayReward)

        // If VIP, give it TWICE immediately
        if (isVIP) {
            applyReward(todayReward)
        }

        claimDailyReward()
        setClaimedToday(true)
        setShowReward(true)

        // Tutorial: Advance to modal sub-step
        if (tutorialPhase === 'daily') {
            setTutorialSubStep(1)
        }
        // streak update handled in store
    }

    const applyReward = (reward: typeof DAILY_REWARDS[0]) => {
        switch (reward.type) {
            case 'gold':
                addGold(reward.amount)
                break
            case 'gems':
                addGems(reward.amount)
                break
            case 'lootbox':
                addFreeLootboxSpins(reward.amount)
                break
            case 'mega':
                addGems(500)
                addGold(1000)
                addPower(100)
                break
        }
    }

    // Close reward modal
    const handleCloseReward = () => {
        setShowReward(false)
        setCurrentReward(null)

        // Tutorial: Advance to next sub-step after claiming
        if (tutorialPhase === 'daily') {
            setTutorialSubStep(2)
        }
    }

    const handleVIPRedirect = () => {
        setShopTab('passes')
        setScreen('shop')
    }

    const canClaimOfVIPUpgrade = (isVIP && lastDailyClaimType === 'free') || !claimedToday

    return (
        <GameLayout>
            <div className="daily">
                {/* Header */}
                <div className="daily__header">
                    <button className="daily__back" onClick={() => setScreen('city')}>← Zurück</button>
                    <div className="daily__title">
                        <h1>🎁 TÄGLICHE BELOHNUNG</h1>
                        <span className="daily__title-sub">Komm jeden Tag zurück für mehr!</span>
                    </div>
                </div>

                {/* ACTION AREA - MOVE TO TOP */}
                <div className="daily__actions">
                    <button
                        className={`btn ${!canClaimOfVIPUpgrade ? 'btn--ghost' : 'btn--gold'} btn--lg btn--full`}
                        onClick={handleClaim}
                        disabled={!canClaimOfVIPUpgrade}
                    >
                        {lastDailyClaimType === 'doubled'
                            ? '✓ Alles abgeholt!'
                            : (lastDailyClaimType === 'free' && isVIP
                                ? '💎 VIP BONUS JETZT ABHOLEN!'
                                : (lastDailyClaimType === 'free'
                                    ? '✓ Heute abgeholt'
                                    : '🎁 Belohnung abholen!'))}
                    </button>
                </div>

                {/* VIP Hint / Upsell */}
                {!isVIP && (
                    <div className="daily__vip-hint">
                        <div style={{ textAlign: 'left' }}>
                            <strong>👑 VIP WERDEN & VERDOPPELN</strong>
                            <div style={{ fontSize: '10px', opacity: 0.8 }}>Erhalte täglich 2x Belohnungen!</div>
                        </div>
                        <button
                            className="btn btn--gem btn--sm"
                            onClick={handleVIPRedirect}
                        >
                            Zum VIP Shop
                        </button>
                    </div>
                )}
                {isVIP && lastDailyClaimType !== 'doubled' && claimedToday && (
                    <div className="daily__vip-claim-box">
                        <button
                            className="btn btn--gold btn--full btn--shine"
                            onClick={handleClaim}
                            style={{
                                background: 'linear-gradient(135deg, #FFD700 0%, #B8860B 100%)',
                                color: '#000',
                                marginBottom: '10px'
                            }}
                        >
                            ✨ Verpassten VIP Bonus abholen!
                        </button>
                    </div>
                )}

                {/* Streak Info */}
                <div className="daily__streak">
                    <span className="daily__streak-label">Login-Streak</span>
                    <span className="daily__streak-value">🔥 Tag {streak}</span>
                </div>

                {/* Reset Timer */}
                <div className="daily__timer">
                    <span>Nächste Belohnung in:</span>
                    <span className="daily__timer-value">{timeUntilReset}</span>
                </div>

                {/* Reward Calendar */}
                <div className="daily__calendar">
                    {DAILY_REWARDS.map((reward, idx) => {
                        const isCurrent = idx === (streak - 1) % 7
                        const isClaimed = idx < (streak - 1) % 7 || (isCurrent && claimedToday)
                        const isLocked = idx > (streak - 1) % 7

                        // Check if this specific day was claimed doubled or free
                        // Note: visual simplification, we assume past days are fully claimed
                        // Only current day shows partial state
                        const showCheck = isClaimed
                        const showDoubleCheck = isClaimed && (idx < (streak - 1) % 7 || lastDailyClaimType === 'doubled')

                        return (
                            <div
                                key={reward.day}
                                id={isCurrent && tutorialPhase === 'daily' ? 'daily-claim-btn' : undefined}
                                className={`daily__day ${isCurrent ? 'daily__day--current' : ''} ${isClaimed ? 'daily__day--claimed' : ''} ${isLocked ? 'daily__day--locked' : ''}`}
                                onClick={() => isCurrent && canClaimOfVIPUpgrade ? handleClaim() : null}
                                style={{ cursor: isCurrent && canClaimOfVIPUpgrade ? 'pointer' : 'default' }}
                            >
                                <span className="daily__day-number">Tag {reward.day}</span>
                                <span className="daily__day-emoji">{reward.emoji}</span>
                                <span className="daily__day-name">{reward.name}</span>
                                {showCheck && (
                                    <div className="daily__day-check">
                                        {showDoubleCheck && isVIP ? '2x' : '✓'}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>


                {/* Reward Modal - Premium Style */}
                {showReward && currentReward && (
                    <div className="modal-overlay" onClick={handleCloseReward}>
                        <div className="daily-modal" onClick={e => e.stopPropagation()}>
                            <div className="daily-modal__header">
                                <div className="daily-modal__icon-wrapper">
                                    <span className="daily-modal__icon">🎉</span>
                                </div>
                                <h2>BELOHNUNG ERHALTEN!</h2>
                            </div>

                            <div className="daily-modal__content">
                                <div className="daily-modal__reward-card">
                                    <div className="daily-modal__glow"></div>
                                    <span className="daily-modal__emoji">{currentReward.emoji}</span>
                                    <span className="daily-modal__amount">
                                        {currentReward.type === 'gold' && '+'}
                                        {currentReward.type === 'gems' && '+'}
                                        {currentReward.amount && currentReward.amount > 0 ? currentReward.amount : ''}
                                    </span>
                                    <span className="daily-modal__name">{currentReward.name}</span>
                                </div>

                                {streak < 7 && (
                                    <div className="daily-modal__hint">
                                        <span>Morgen: <strong>Tag {streak + 1}</strong> Belohnung!</span>
                                    </div>
                                )}
                                {streak === 7 && (
                                    <div className="daily-modal__mega">
                                        🏆 MEGA-BELOHNUNG FREIGESCHALTET!
                                    </div>
                                )}
                            </div>

                            <div className="daily-modal__actions">
                                <button
                                    id="daily-ok-btn"
                                    className="btn btn--gold btn--lg btn--full btn--shine"
                                    onClick={handleCloseReward}
                                >
                                    SUPER!
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </GameLayout>
    )
}
